import { ALL_RECORD_TYPE_IDS, ALL_RECORD_TYPES } from 'constants/recordTypes';
import { GenericResource } from 'types/api';
import { useSetDocument, useGetCommentAnnotations } from '@veltdev/react';
import { RecordType } from 'types/apps';
import SidebarInfoSection from 'ui-component/DetailLayout/SidebarComponents/SidebarInfoSection';
import { IconMessage } from '@tabler/icons-react';
import { pluralizeNoun } from 'utils/functions';
import _ from 'lodash';
import { InfoSectionItemFormatTypes } from 'ui-component/InfoSectionItem';
import { useContext } from 'react';
import { DrawerContext } from 'ui-component/DetailLayout';
import SegmentIconButton from 'ui-component/Segment/SegmentIconButton';

export interface VeltCommentsSidebarInfoSectionProps<
  T extends GenericResource
> {
  record: T;
  recordType: ALL_RECORD_TYPE_IDS;
}

const VeltCommentsSidebarInfoSection = <T extends GenericResource>({
  record,
  recordType,
}: VeltCommentsSidebarInfoSectionProps<T>) => {
  const { setCommentMode, setOpen } = useContext(DrawerContext);

  const recordTypeDefinition = recordType
    ? (ALL_RECORD_TYPES[recordType] as unknown as RecordType<T>)
    : null;

  useSetDocument(record.id, {
    documentName: recordTypeDefinition?.nameGetter?.(record) || '',
  });

  const allComments = useGetCommentAnnotations({ documentIds: [record.id] });
  const isLoadingComments = allComments.data === null;
  const comments = _.chain(allComments.data?.[record.id])
    .filter((annotation) => annotation.status.type !== 'terminal')
    .map('comments')
    .flatten()
    .value();
  const commentCount = comments.length;
  const lastComment = _.chain(comments).sortBy('createdAt').last().value();

  const handleSetCommentMode = () => {
    setCommentMode(true);
    setOpen(true);
  };

  return (
    <SidebarInfoSection
      caption="Comments"
      count={commentCount}
      Icon={IconMessage}
      color="primary"
      visible
      alwaysVisible
      collapsedTooltip={`${commentCount} ${pluralizeNoun(
        'comment',
        commentCount
      )}`}
      formatType={InfoSectionItemFormatTypes.string}
      value={`${
        lastComment?.commentText
          ? lastComment.commentText.length > 80
            ? `${lastComment.commentText.substring(0, 80)}...`
            : lastComment.commentText
          : ''
      }${commentCount > 1 ? ` + ${commentCount - 1} more` : ''}`}
      typographyProps={{
        variant: 'body1',
      }}
      onIconClick={handleSetCommentMode}
      clipboard={false}
      onClick={handleSetCommentMode}
      isLoading={isLoadingComments}
    >
      <SegmentIconButton
        onClick={() => {
          setCommentMode(true);
          setOpen(true);
        }}
        size="small"
        color="primary"
        tooltip="View & add comments"
      >
        <IconMessage width={20} height={20} stroke={1.5} />
      </SegmentIconButton>
    </SidebarInfoSection>
  );
};

export default VeltCommentsSidebarInfoSection;
